import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
} from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '../services/firebase';
import { sortTemplatesByPetitionTypeOrder } from '../config/orders';
import { getClientId } from '../config/domain';
import { useAuth0Firebase } from '../hooks/useAuth0Firebase';

const DocumentTemplatesContext = createContext(null);

export const DocumentTemplatesProvider = ({ children }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track whether we have already attempted a successful fetch, so we don't
  // spam the network each time auth state changes (e.g. token refresh).
  const hasFetchedSuccessfully = useRef(false);

  // Firebase authentication status coming from the shared hook
  const { isFirebaseAuthenticated, isLoading: authLoading } =
    useAuth0Firebase();

  // Define fetchTemplates outside of useEffect
  const fetchTemplates = async () => {
    try {
      console.log('Starting to fetch templates from Firestore...');
      setLoading(true);

      // Use the exported firestore instance (client-scoped: templates/{clientId}/templates)
      const clientId = getClientId();
      const templatesCollection = collection(firestore, 'templates', clientId, 'templates');

      // Create a query for templates that aren't deleted
      let templatesQuery;
      try {
        templatesQuery = query(
          templatesCollection,
          where('deleted', '!=', true),
        );
      } catch (queryError) {
        console.error('Error creating Firestore query:', queryError);
        // Fallback to getting all templates if query fails
        templatesQuery = templatesCollection;
      }

      console.log('Executing Firestore query...');
      const templatesSnapshot = await getDocs(templatesQuery);

      console.log('Query complete. Documents found:', templatesSnapshot.size);

      // Detailed logging of raw documents
      if (templatesSnapshot.empty) {
        console.log('No templates found in Firestore');
      } else {
        console.log(
          'Raw template docs:',
          templatesSnapshot.docs.map((doc) => ({
            id: doc.id,
            data: doc.data(),
          })),
        );
      }

      // Process the documents
      const templatesList = templatesSnapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name || `Template ${doc.id}`, // Provide fallback name
        titles: doc.data().titles || {}, // Ensure titles exist
        ...doc.data(),
      }));

      // Sort templates according to petition type order
      const sortedTemplates = await sortTemplatesByPetitionTypeOrder(
        templatesList,
      );

      console.log('Processed templates:', templatesList);
      console.log('Sorted templates by petition type order:', sortedTemplates);
      setTemplates(sortedTemplates);
      setError(null);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(`Failed to load document templates: ${err.message}`);
      // Set empty templates array to prevent undefined errors
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch templates only after we are sure the user is authenticated with
  // Firebase (and not while Auth logic itself is still loading). If the user
  // logs out we keep the previous templates in memory, but we allow a new
  // fetch if they log back in.
  useEffect(() => {
    if (
      !authLoading &&
      isFirebaseAuthenticated &&
      !hasFetchedSuccessfully.current
    ) {
      fetchTemplates().then(() => {
        hasFetchedSuccessfully.current = true;
      });
    }
  }, [authLoading, isFirebaseAuthenticated]);

  const value = {
    templates,
    loading,
    error,
    refetch: fetchTemplates, // Expose refetch function to consumers
  };

  return (
    <DocumentTemplatesContext.Provider value={value}>
      {children}
    </DocumentTemplatesContext.Provider>
  );
};

export const useDocumentTemplates = () => {
  return useContext(DocumentTemplatesContext);
};
